import { Injectable } from '@angular/core';
import { APIService } from './api.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BuildingReview } from 'app/classes/building-review';
import { DateService } from './date.service';
import { firstValueFrom } from 'rxjs';
import { Gateway } from 'app/classes/gateway';
import { Org } from 'app/classes/org';
import { Site } from 'app/classes/site';
import { User } from 'app/classes/user';
import { IIssueEntity, Issue } from "app/classes/issue";

@Injectable({
  providedIn: 'root'
})
export class IssueTrackingService {

  static readonly API_URL = 'https://eapkrq89fk.execute-api.eu-west-2.amazonaws.com/v1';

  constructor(private apiService: APIService, private http: HttpClient,) { }

  getStats(): Promise<IIssueTrackingStats> {
    const params = new HttpParams().set('a', 'get-stats');

    return firstValueFrom(this.http
      .get<any>(IssueTrackingService.API_URL, { headers: this.apiService.getUAOHeaders().headers, params }))
      .then((response) => {
        return <IIssueTrackingStats>{ counts: { avg_time_to_close: response.avgTimeToComplete, closed_last_28: response.closedLast28Count, new_last_28: response.openLast28Count, open: response.openCount } };
      });
  }

  createIssue(issue: Issue): Promise<any> {
    const params = new HttpParams().set('a', 'issue-create');

    return firstValueFrom(this.http
      .post<any[]>(IssueTrackingService.API_URL, JSON.stringify(issue), { headers: this.apiService.getUAOHeaders().headers, params }))
      .then((response) => {
        return response;
      });
  };

  updateEntitiesForIssue(issueId, changes: IIssueEntitiesChanges): Promise<any> {
    const params = new HttpParams().set('a', 'issue-update-entities').set('id', issueId);
    const options = { headers: this.apiService.getUAOHeaders().headers, params };

    return firstValueFrom(this.http
      .post<any[]>(IssueTrackingService.API_URL, changes, options))
      .then((response) => {
        return response;
      });
  }

  updateIssue(issueId: number, payload: any): Promise<any> {
    const params = new HttpParams().set('a', 'issue-update').set('id', issueId);
    const options = { headers: this.apiService.getUAOHeaders().headers, params };

    return firstValueFrom(this.http
      .post<any[]>(IssueTrackingService.API_URL, payload, options))
      .then((response) => {
        return response;
      });
  };

  completeIssue(issueId: number, payload: { status_id: number, completedNotes: string }): Promise<any> {
    const params = new HttpParams().set('a', 'issue-complete').set('id', issueId);
    const options = { headers: this.apiService.getUAOHeaders().headers, params };

    if (!payload.completedNotes) {
      throw new Error('No notes');
    }
    return firstValueFrom(this.http
      .post<any[]>(IssueTrackingService.API_URL, payload, options))
      .then((response) => {
        return response;
      });
  };

  addAssetsFromReview(review: BuildingReview, assetIds: number[]): Promise<any> {
    if (!assetIds?.length) {
      throw new Error('No asset ids');
    }
    const payload = {
      id: review.id,
      buildingId: review.building.id,
      start: DateService.dbDate(review.review.dates.start),
      end: DateService.dbDate(review.review.dates.end),
      assetIds
    };
    return this.post<any>(payload, 'reviews/add');
  }

  addReview(review: BuildingReview): Promise<any> {
    const payload = {
      buildingId: review.building.id,
      start: DateService.dbDate(review.review.dates.start),
      end: DateService.dbDate(review.review.dates.end)
    };
    return this.post<any>(payload, 'reviews/add');
  }

  setStatus(id: number, status: IssueTrackingStatusType, proposedAction: string) {
    return new Promise((resolve) => {
      this.post<any>({ status, proposedAction }, '', `id=${id}&a=status-set`).then(response => {
        resolve(response);
      });
    });
  }

  addComment(id: number, comment: string, commentType: IssueTrackingCommentType, options = {}): Promise<any> {
    const payload = {
      comment,
      commentType,
      options
    };

    return this.post<any>(payload, '', `id=${id}&a=comment-add`);
  }

  deleteComment(id, comment): Promise<any> {

    return this.post<any>({ comment }, '', `id=${id}&a=comment-del`);
  }

  getAudit(uuid: string): Promise<IGetIssueResponse> {
    return new Promise((resolve) => {
      this.get<IGetIssuesResponse>('audit', `uuid=${uuid}`).then(auditResponse => {

        resolve(auditResponse);
      });
    });
  }

  getIssue(id: number): Promise<Issue> {
    return new Promise((resolve) => {
      this.get<IGetIssuesResponse>('', `id=${id}`).then(issueResponse => {
        const points = issueResponse?.issue?.points;
        if (points) {
          points.forEach(point => {
            point.fromDate = new Date(point.fromDate + ' 00:00:00');
            point.toDate = new Date(point.toDate + ' 23:59:59');
          });
        }

        const issue = new Issue(issueResponse.issue);

        resolve(issue);
      });
    });
  }

  deleteIssue(id: number): Promise<any> {
    return new Promise((resolve) => {
      this.delete<any>('', `a=issue-del&id=${id}`).then(issueResponse => {

        resolve(issueResponse);
      });
    });
  }

  getIssues(state: 'Open' | 'Closed' | 'All' = 'All', visibility: 'All' | 'Public' | 'Private' = 'All'): Promise<IGetIssuesResponse> {
    const params = new HttpParams().set('for', 'sites').set('state', state.toLowerCase()).set('visibility', visibility.toLowerCase());
    const options = { headers: this.apiService.getUAOHeaders().headers, params };

    return firstValueFrom(this.http
      .get<IGetIssuesResponse>(IssueTrackingService.API_URL, options))
      .then((issuesResponse) => {
        if (issuesResponse.issues) {
          issuesResponse.issues.forEach(issue => {
            issue.assetCount = issue.assetTitles?.length ?? 0;
            issue.reviewDate = new Date(issue.reviewDate);
            issue.createdAt = new Date(issue.createdAt);

            if (issue.completedAt) {
              issue.completedAt = new Date(issue.completedAt);
            }
            if (issue.resolvedAt) {
              issue.resolvedAt = new Date(issue.resolvedAt);
            }
            if (issue.lastCommentDate) {
              const value: any = issue.lastCommentDate;
              if (!value) return null;

              const [datePart, timePart = '00:00'] = value.split(/\s+/);
              const [day, month, year] = datePart.split('-').map(Number);
              const [hours, minutes] = timePart.split(':').map(Number);

              if (
                isNaN(day) || isNaN(month) || isNaN(year) ||
                isNaN(hours) || isNaN(minutes) ||
                day < 1 || day > 31 ||
                month < 1 || month > 12 ||
                year < 1900 || year > 2100
              ) {
                return null;
              }

              const date = new Date(year, month - 1, day, hours, minutes, 0);
              issue.lastCommentDate = new Date(date);
            }
            if (issue.update_info.updatedAt) {
              issue.update_info.updatedAt = new Date(issue.update_info.updatedAt);
            }
          });
        }

        return issuesResponse;
      });
  }

  getSubStatuses() {
    return [
      { "id": 1, "title": "Created", "status_id": "open" },
      { "id": 2, "title": "In Progress", "status_id": "in progress" },
      { "id": 3, "title": "In Quote", "status_id": "in progress" },
      { "id": 4, "title": "On Hold", "status_id": "in progress" },
      { "id": 5, "title": "Awaiting Works", "status_id": "in progress" },
      { "id": 6, "title": "Resolved", "status_id": "completed" },
      { "id": 7, "title": "Partially Resolved", "status_id": "completed" },
      { "id": 8, "title": "Not Required", "status_id": "cancelled" },
      { "id": 9, "title": "Unable to Resolve", "status_id": "cancelled" }
    ];
  }
  getOptionsForStatusWhenCompleting(): { id: number, title: string }[] {
    const subStatuses = this.getSubStatuses();

    const options: { id: number, title: string }[] = [];

    ['completed', 'cancelled'].forEach(status => {

      subStatuses.filter(s => s.status_id === status).forEach(subStatus => {
        options.push({
          "id": subStatus.id,
          "title": `${status} -> ${subStatus.title}`.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
        });
      });

    });

    return options;
  }



  getOptionsForStatusBeforeComplete(): { id: number, title: string }[] {
    const subStatuses = this.getSubStatuses();

    const options: { id: number, title: string }[] = [];

    ['open', 'in progress'].forEach(status => {

      subStatuses.filter(s => s.status_id === status).forEach(subStatus => {
        options.push({
          "id": subStatus.id,
          "title": `${status} -> ${subStatus.title}`.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
        });
      });

    });

    return options;
  }

  getOptionsForStatus(): { id: number, title: string }[] {
    const subStatuses = this.getSubStatuses();

    const options: { id: number, title: string }[] = [];

    ['open', 'in progress', 'completed', 'cancelled'].forEach(status => {

      subStatuses.filter(s => s.status_id === status).forEach(subStatus => {
        options.push({
          "id": subStatus.id,
          "title": `${status} -> ${subStatus.title}`.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
        });
      });

    });

    return options;
  }

  getAdmin(): Promise<any> {
    return this.get<any>('admin');
  }

  setAdmin(data: any): Promise<any> {
    return this.post<any>(data, 'admin');
  }

  enableForOrg(): Promise<any> {
    return this.post<any>({ isEnabled: true }, 'admin');
  }

  disableForOrg(): Promise<any> {
    return this.post<any>({ isEnabled: false }, 'admin');
  }

  get<T>(path: string = '', qs: string = ''): Promise<any> {
    return new Promise((resolve) => {
      const url = `${IssueTrackingService.API_URL}/${path}?${qs}`;
      return this.http
        .get<T>(url, this.apiService.getUAOHeaders())
        .subscribe(b => {
          resolve(b);
        });
    });
  }

  delete<T>(path: string = '', qs: string = ''): Promise<any> {
    return new Promise((resolve) => {
      const url = `${IssueTrackingService.API_URL}/${path}?${qs}`;
      return this.http
        .delete<T>(url, this.apiService.getUAOHeaders())
        .subscribe(b => {
          resolve(b);
        });
    });
  }

  post<T>(body, path: string = '', qs: string = ''): Promise<any> {
    return new Promise((resolve) => {
      const url = `${IssueTrackingService.API_URL}/${path}?${qs}`;
      return this.http
        .post<T>(url, body, this.apiService.getUAOHeaders())
        .subscribe(b => {
          resolve(b);
        });
    });
  }

  getAllowedAssignees(): Promise<User[]> {



    return new Promise((resolve) => {

      return this.apiService.getUsersForOrg().then(users => {

        resolve(users);
      });
    });
  }
}

export interface IGetIssuesResponse {
  issues?: ITrackingIssue[];
  master: { isEnabled: boolean };
}

export interface ITrackingIssue {
  id: number;
  assignee: number;
  assigneeName: string;
  annotation: string;
  documentKey: string;
  assetTitle: string;
  assetTitles: string[];
  assetCount: number;
  assetType_id: number;
  asset_id: number;
  buildingTitle: string;
  rag: string;
  building_id: number;
  createdAt: Date;
  completedAt: Date;
  completedBy: number;
  completedNotes: string;
  fingerprint: string;
  createdBy: number;
  createdByName: string;
  document_id: number;
  description: string;
  title: string;
  issueRag: string;
  issueType: IssueTrackingType;
  noOfMonths: number;
  proposedAction: string;
  orgShortTitles: string[];
  orgShortTitle: string; // The issue org
  reviewDate: Date;
  uuid: string; // the review uuid
  issueUUID: string;
  state: string;
  status: IssueTrackingStatusType;
  subStatusTitle: string;
  subStatus_id: number;
  siteTitle: string;
  siteId: number;
  resolution: string;
  resolvedAt: Date;
  comments: IIssueTrackingComment[];
  audits: ITrackingIssueAudit[];
  entities: ITrackingIssueEntity[];
  update_info: { updatedAtDays: number, updatedAt: Date };
  lastComment?: string;
  lastCommentDate?: Date;
  lastCommentType?: IssueTrackingCommentType;
}

export interface IGetIssueResponse {
  issue: ITrackingIssue;
  master?: { isEnabled: boolean };
}

export interface IIssueTrackingComment {
  id: number;
  comment: string;
  createdBy: number;
  createdByName: string;
  createdAt: Date;
  isMyComment: boolean;
  commentType: IssueTrackingCommentType;
  isEnabled: boolean;
  importance: 'normal' | 'high';
}

export interface ITrackingIssueAudit {
  id: number;
  action: string;
  createdAt: Date;
  data: any;
  createdBy: number;
  createdByName: string;
  message: string;
}

export interface ITrackingIssueEntity {
  id: number;
  building_review_id: number;
  site_id: number;
  asset_id: number;
}

export interface IAuditItem {
  id: number;
  action: 'created' | 'status' | 'comment' | 'closed';
}

export interface ITrackingIssueEntityItem {
  type: 'org' | 'gateway' | 'site' | 'user';
  obj: Org | Gateway | Site | User;
}

export interface ITrackingIssuePointItem {
  fromDate: Date;
  toDate: Date;
  asset_id: number;
}

export interface IIssueTrackingStats {
  counts: {
    open: number,
    new_last_28: number,
    closed_last_28: number,
    avg_time_to_close: number
  }
}

export interface IIssueEntitiesChanges {
  add?: IIssueEntity[];
  remove?: any[];
}

export type IssueTrackingStatusType = 'created' | 'open' | 'in progress' | 'completed' | 'no action required' | 'cancelled';
export type IssueTrackingType = 'building_review' | 'manual';
export type IssueTrackingProgressType = "Analysis" | "Development" | "Testing" | "Review" | "Documentation" | "Waiting feedback" | "Implementing" | "Monitoring" | "Training" | "Configuration" | "Maintenance" | "Not Started";
export type IssueTrackingCommentType = 'question' | 'answer' | 'contractor' | 'fm' | '4d' | null;
